import { useEffect, useMemo, useState } from "react";
import { GoSync } from "react-icons/go";
import { useTranslation } from "react-i18next";
import Banner from "../components/Banner";
import Card from "../components/Card";
import LinkButton from "../components/LinkButton";
import { RowSpinner } from "../components/Loader/Loader";
import PrimaryButton from "../components/PrimaryButton";
import { AccountCard } from "../components/UploadedAccount";
import UploadedAccounts from "../components/UploadedAccounts";
import { ENABLE_SYNC_UNIT_CONTROLLER } from "../constants/constants";
import { MY_ACCOUNTS_PATH } from "../constants/syncRoutes";
import useNavigation from "../hooks/use-navigation";
import useToken from "../hooks/use-token";
import {
  useFinalizeInvestingAccountsSynchronizationMutation,
  useReadSynchronizingInvestingAccountsQuery,
  useSynchronizeFilesMutation,
  useSynchronizingFilesQuery,
} from "../store";
import { UNIFIED_SYNC_CACHE_KEY, UNIFIED_UPLOAD_PATH } from "./UnifiedUploadPage";

// Hooks cannot run in a loop of variable length, so the known providers are a fixed list.
const IB = "INTERACTIVE_BROKERS";
const FF = "FREEDOM_FINANCE";

function mergeById(lists) {
  const byId = new Map();
  lists.flat().forEach((account) => byId.set(account.id, account));
  return [...byId.values()];
}

function LegacyUnifiedUploadedAccountsPage() {
  const { t } = useTranslation();
  const { navigate } = useNavigation();
  const token = useToken();
  // SCR-01 stores the synchronize response here (fixedCacheKey), so it shows at once.
  const [, { data: syncResponse, reset: forgetSyncResponse }] = useSynchronizeFilesMutation({ fixedCacheKey: UNIFIED_SYNC_CACHE_KEY });
  // The hand-off is one-shot: leaving SCR-03 drops it so a later direct open never shows stale accounts.
  useEffect(() => () => forgetSyncResponse(), [forgetSyncResponse]);
  const responseAccounts = useMemo(() => syncResponse?.synchronizingInvestingAccounts || [], [syncResponse]);

  const ib = useSynchronizingFilesQuery({ params: { dataProvider: IB } }, { skip: !token, refetchOnMountOrArgChange: true });
  const ff = useSynchronizingFilesQuery({ params: { dataProvider: FF } }, { skip: !token, refetchOnMountOrArgChange: true });
  const queries = [ib, ff].filter((q) => !q.isUninitialized);

  // The synchronize response bridges only until a provider's poll answers; after that the poll is the truth.
  const accounts = useMemo(() => {
    const forProvider = (provider, query) =>
      query.data
        ? query.data.synchronizingInvestingAccounts || []
        : responseAccounts.filter((a) => a.dataProvider === provider);
    return mergeById([forProvider(IB, ib), forProvider(FF, ff)]);
  }, [responseAccounts, ib, ff]);
  const refetchAll = () => queries.forEach((q) => q.refetch());

  const back = (
    <LinkButton to={UNIFIED_UPLOAD_PATH} className="mb-5">
      <span aria-hidden="true">←</span>
      {t("reportInstructions.back")}
    </LinkButton>
  );

  let content;
  if (accounts.length > 0) {
    content = (
      <UploadedAccounts
        synchronizingData={{ synchronizingInvestingAccounts: accounts }}
        refetchData={refetchAll}
        onAddFile={() => navigate(UNIFIED_UPLOAD_PATH)}
        embedded
        title={t("unifiedUpload.accounts.title")}
      />
    );
  // Loading lasts until the first provider answers; the others join the list as they arrive.
  } else if (queries.length === 0 || queries.every((q) => q.isLoading)) {
    content = (
      <div>
        <GoSync className="animate-spin inline-block text-green-700" />
      </div>
    );
  } else if (queries.some((q) => q.error)) {
    content = <div>Error loading...</div>;
  } else {
    content = (
      <div className="mt-8">
        <p className="text-sm">{t("unifiedUpload.accounts.empty")}</p>
        <LinkButton to={UNIFIED_UPLOAD_PATH} className="mt-2">
          {t("unifiedUpload.accounts.toSubmission")}
        </LinkButton>
      </div>
    );
  }

  return (
    <div className="page">
      {back}
      {content}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// broker-report-sync SCR-02: one list of every provider (AC-05), polled every 3 s while a row prepares (ADR-0006).

export const SYNC_POLLING_INTERVAL_MS = 3000;

// «Підтвердити всі»: at least two rows, none unreadable, every one SYNCED (AC-15, AC-15b).
export const canConfirmAll = (rows) =>
  rows.length >= 2 && rows.every((row) => !row.readError && row.syncUnitStatus === "SYNCED");

function CenteredCard({ children }) {
  return (
    <Card data-testid="centered-card" className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-sm text-text-muted">
      {children}
    </Card>
  );
}

function SyncUnitsPage({ pollingInterval = SYNC_POLLING_INTERVAL_MS }) {
  const { t } = useTranslation();
  const token = useToken();
  const { navigate, locationState, clearLocationState } = useNavigation();
  // A banner handed over by SCR-03 is shown once.
  const [stale] = useState(() => locationState?.stale || null);
  // SCR-01 hands over the reports it could not add: their account is still preparing (Flow US-07).
  const [deferred, setDeferred] = useState(() => locationState?.deferred || null);
  useEffect(() => {
    if (locationState?.stale || locationState?.deferred) clearLocationState?.();
  }, [locationState, clearLocationState]);

  const [finalize, { isLoading: confirming }] = useFinalizeInvestingAccountsSynchronizationMutation();
  const [confirmError, setConfirmError] = useState(null); // "changed" | "failed"

  const [polling, setPolling] = useState(false);
  const { data, error, isLoading, refetch } = useReadSynchronizingInvestingAccountsQuery(
    undefined,
    { skip: !token, refetchOnMountOrArgChange: true, pollingInterval: polling && !confirming ? pollingInterval : 0 },
  );
  const rows = useMemo(() => data?.synchronizingInvestingAccounts || [], [data]);
  useEffect(() => setPolling(rows.some((row) => !row.readError && row.syncUnitStatus === "SYNCING")), [rows]);

  const confirmAll = () => {
    setConfirmError(null);
    finalize({ investingAccounts: rows.map((row) => ({ id: row.id, syncUnitId: row.syncUnitId })) })
      .unwrap()
      .then(() => navigate(MY_ACCOUNTS_PATH, { state: { toast: t("syncUnits.confirmAll.success", { count: rows.length }) } }))
      .catch((failure) => setConfirmError(failure?.status === 409 || failure?.status === 404 ? "changed" : "failed"));
  };

  let content;
  if (isLoading || (!data && !error)) {
    content = (
      <CenteredCard>
        <RowSpinner />
        {t("syncUnits.loading")}
      </CenteredCard>
    );
  } else if (!data) {
    content = (
      <Banner tone="danger">
        <p>{t("syncUnits.error")}</p>
        <LinkButton onClick={refetch}>{t("syncUnits.retry")}</LinkButton>
      </Banner>
    );
  } else if (rows.length === 0) {
    content = (
      <CenteredCard>
        {t("syncUnits.empty")}
        <LinkButton to={UNIFIED_UPLOAD_PATH}>{t("syncUnits.toSubmission")}</LinkButton>
      </CenteredCard>
    );
  } else {
    content = (
      <>
        <ul
          aria-label={t("syncUnits.title")}
          aria-busy={confirming || undefined}
          className={`flex flex-col gap-3 ${confirming ? "pointer-events-none opacity-60" : ""}`}
        >
          {rows.map((row) => (
            <AccountCard key={row.id} account={row} />
          ))}
        </ul>
        {confirmError === "failed" && <Banner tone="danger">{t("syncUnits.confirmAll.error")}</Banner>}
        {canConfirmAll(rows) && (
          <div className="flex flex-col items-start gap-2">
            <PrimaryButton loading={confirming} onClick={confirmAll} className="h-[52px]">
              {t("syncUnits.confirmAll.action")}
            </PrimaryButton>
            <p className="text-xs text-text-muted">{t("syncUnits.confirmAll.hint")}</p>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-5 md:px-10 py-8 max-w-[1040px]" data-testid="sync-units-page">
      <h1 className="text-2xl font-semibold tracking-tight">{t("syncUnits.title")}</h1>
      <p className="text-sm text-text-muted">{t("syncUnits.explanation")}</p>
      {data && error && <p className="text-xs text-warn">{t("syncUnits.pollingError")}</p>}
      {stale && (
        <Banner tone="warn">
          {stale.status === 404 ? t("syncUnits.stale.notFound") : t("syncUnits.stale.changed", { account: stale.account })}
        </Banner>
      )}
      {deferred && (
        <Banner tone="warn">
          <p>{t("syncUnits.deferred", deferred)}</p>
          <LinkButton onClick={() => setDeferred(null)}>{t("syncUnits.close")}</LinkButton>
        </Banner>
      )}
      {confirmError === "changed" && (
        <Banner tone="warn">
          <p>{t("syncUnits.confirmAll.changed")}</p>
          <LinkButton onClick={() => setConfirmError(null)}>{t("syncUnits.close")}</LinkButton>
        </Banner>
      )}
      {content}
    </div>
  );
}

// SCR-02: with ENABLE_SYNC_UNIT_CONTROLLER — the broker-report-sync list; without — the unified upload screen as is.
function UnifiedUploadedAccountsPage(props) {
  return ENABLE_SYNC_UNIT_CONTROLLER ? <SyncUnitsPage {...props} /> : <LegacyUnifiedUploadedAccountsPage />;
}

export default UnifiedUploadedAccountsPage;
